import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { getCompanyMessaging } from '../services/company';
import { configurePhoneDefaults, DEFAULT_PHONE_COUNTRY, DEFAULT_PHONE_COUNTRY_CODE } from '../utils/phone';

const TRACKING_KEYS = ['confirmed', 'scheduled', 'ready', 'completed'];

/**
 * Company messaging settings (phone dial code, invoice message template),
 * cached for the session. Mounted once in the dashboard shell so the phone
 * helpers know the dial code before any WhatsApp / Viber link is built;
 * also used directly by the Send-invoice panel.
 */
const useCompanyMessaging = () => {
    const { t } = useTranslation();
    const { data, isLoading } = useQuery(['company', 'messaging'], getCompanyMessaging, {
        staleTime: 5 * 60 * 1000,
        refetchOnWindowFocus: false,
    });

    const settings = data?.code === 200 && data.message ? data.message : null;
    const phoneCountryCode = settings?.phoneCountryCode || DEFAULT_PHONE_COUNTRY_CODE;
    const phoneCountry = settings?.phoneCountry || DEFAULT_PHONE_COUNTRY;
    const invoiceMessageTemplate = settings?.invoiceMessageTemplate || t('defaultMessages.invoice');

    const trackingEnabled = settings?.trackingEnabled === true;
    const reviewRequestEnabled = settings?.reviewRequestEnabled === true;
    const googleReviewUrl = settings?.googleReviewUrl || null;
    const trackingMessages = Object.fromEntries(
        TRACKING_KEYS.map((key) => [key, settings?.trackingMessages?.[key]?.trim() || t(`defaultMessages.tracking${key.charAt(0).toUpperCase() + key.slice(1)}`)])
    );

    useEffect(() => {
        configurePhoneDefaults({ country: phoneCountry, countryCode: phoneCountryCode });
    }, [phoneCountry, phoneCountryCode]);

    return {
        phoneCountry, phoneCountryCode, invoiceMessageTemplate,
        trackingEnabled, reviewRequestEnabled, googleReviewUrl, trackingMessages,
        isLoading, isLoaded: !!settings,
    };
};

export default useCompanyMessaging;
